/**
 * Types and functions for making an extrusion that follows 3 PolyLine3 rails. Userul for roofs.
 * @module
 */
import type { Face } from "./brep";
import { faceByFourPoints, faceByNPoints, faceByThreePoints } from "./brep";
import { DIST_EPSILON } from "./core";
import { planeMove } from "./plane";
import type { Plane } from "./planeTypes";
import { polyLine2Move } from "./polyLine2";
import type { PolyLine2 } from "./polyLine2Types";
import type { PolyLine3 } from "./polyLine3";
import { polyLine3Copy, polyLine3ExtendEnd, polyLine3ExtendStart, polyLine3OnPlane } from "./polyLine3";

export interface ThreeRailExtrusion {
  startFace: Face | null;
  startHipFace?: Face;
  leftFaces: Face[];
  rightFaces: Face[];
  leftMassFaces: Face[];
  rightMassFaces: Face[];
  bottomFaces: Face[];
  endFace: Face | null;
  endHipFace?: Face;
}

export function threeRailExtrusionToFaces(ext: ThreeRailExtrusion): Face[] {
  const { leftFaces, rightFaces, bottomFaces, startFace, endFace, startHipFace, endHipFace } = ext;

  const faces: Face[] = [...leftFaces, ...rightFaces, ...bottomFaces];

  if (startFace) {
    faces.push(startFace);
  }

  if (endFace) {
    faces.push(endFace);
  }

  if (startHipFace) {
    faces.push(startHipFace);
  }

  if (endHipFace) {
    faces.push(endHipFace);
  }

  return faces;
}

export function extrudeAlongThreeRails(
  peakRail: PolyLine2,
  leftRail: PolyLine2,
  rightRail: PolyLine2,
  basePlane: Plane,
  peakPlane: Plane,
): ThreeRailExtrusion {
  return extrudeAlongThreeRailsExt(peakRail, leftRail, rightRail, basePlane, peakPlane, 0.0, 0.0);
}

function faceByThreePointsOnRails(left: PolyLine3, right: PolyLine3, peak: PolyLine3, i: number): Face | null {
  const ix = i;
  const iy = i + 1;
  const iz = i + 2;

  return faceByThreePoints(right[ix], right[iy], right[iz], left[ix], left[iy], left[iz], peak[ix], peak[iy], peak[iz]);
}

function faceAlongRailEdges(l3: PolyLine3, r3: PolyLine3, i: number): Face | null {
  const l0x = l3[i + 0];
  const l0y = l3[i + 1];
  const l0z = l3[i + 2];

  const l1x = l3[i + 3];
  const l1y = l3[i + 4];
  const l1z = l3[i + 5];

  const r0x = r3[i + 0];
  const r0y = r3[i + 1];
  const r0z = r3[i + 2];

  const r1x = r3[i + 3];
  const r1y = r3[i + 4];
  const r1z = r3[i + 5];

  return faceByFourPoints(r0x, r0y, r0z, l0x, l0y, l0z, l1x, l1y, l1z, r1x, r1y, r1z);
}

export function extrudeAlongTwoRails(
  bottomRail: PolyLine2,
  topRail: PolyLine2,
  bottomPlane: Plane,
  topPlane: Plane,
): Face[] {
  if (!(bottomRail.length >= 4)) {
    console.assert(false);
  }
  if (!(bottomRail.length === topRail.length)) {
    console.assert(false);
  }

  const faces: Face[] = [];

  const b3 = polyLine3OnPlane(bottomRail, bottomPlane);
  const t3 = polyLine3OnPlane(topRail, topPlane);

  for (let i = 0, l = b3.length - 3; i < l; i += 3) {
    const face = faceAlongRailEdges(b3, t3, i);
    if (!face) {
      continue;
    }
    faces.push(face);
  }

  return faces;
}

export function adjustExtrusionFaceV(face: Face, startV: number) {
  const { loops, plane } = face;

  planeMove(plane, -plane.vX * startV, -plane.vY * startV, -plane.vZ * startV);
  for (let i = 0, l = loops.length; i < l; i++) {
    polyLine2Move(face.loops[i], 0.0, startV);
  }
}

export function extrudeAlongThreeRailsExt(
  peakRail: PolyLine2,
  leftRail: PolyLine2,
  rightRail: PolyLine2,
  basePlane: Plane,
  peakPlane: Plane,
  truncatedStartPeakDist: number,
  truncatedEndPeakDist: number,
): ThreeRailExtrusion {
  if (!(peakRail.length >= 4)) {
    console.assert(false);
  }
  if (!(peakRail.length === leftRail.length && peakRail.length === rightRail.length)) {
    console.assert(false);
  }

  const leftFaces: Face[] = [];
  const rightFaces: Face[] = [];
  const leftMassFaces: Face[] = [];
  const rightMassFaces: Face[] = [];
  const bottomFaces: Face[] = [];

  let startHipFace: Face | null = null;
  let endHipFace: Face | null = null;

  const p3 = polyLine3OnPlane(peakRail, peakPlane);
  const l3 = polyLine3OnPlane(leftRail, basePlane);
  const r3 = polyLine3OnPlane(rightRail, basePlane);

  const startFace = faceByThreePointsOnRails(r3, l3, p3, 0);
  const endFace = faceByThreePointsOnRails(l3, r3, p3, p3.length - 3);

  for (let i = 0, l = p3.length - 3; i < l; i += 3) {
    const bottomFace = faceAlongRailEdges(l3, r3, i);
    if (!bottomFace) {
      continue;
    }
    bottomFaces.push(bottomFace);
  }

  for (let i = 0, l = p3.length - 3; i < l; i += 3) {
    const leftMassFace = faceAlongRailEdges(p3, l3, i);
    if (leftMassFace) {
      leftMassFaces.push(leftMassFace);
    }
    const rightMassFace = faceAlongRailEdges(r3, p3, i);
    if (rightMassFace) {
      rightMassFaces.push(rightMassFace);
    }
  }

  if (truncatedStartPeakDist > DIST_EPSILON) {
    polyLine3ExtendStart(p3, -truncatedStartPeakDist);
  }

  if (truncatedEndPeakDist > DIST_EPSILON) {
    polyLine3ExtendEnd(p3, -truncatedEndPeakDist);
  }

  // do the extensions
  const lp3 = polyLine3Copy(p3);
  const rp3 = polyLine3Copy(p3);

  for (let i = 0, l = p3.length - 3; i < l; i += 3) {
    const lp0x = lp3[i + 0];
    const lp0y = lp3[i + 1];
    const lp0z = lp3[i + 2];

    const lp1x = lp3[i + 3];
    const lp1y = lp3[i + 4];
    const lp1z = lp3[i + 5];

    const rp0x = rp3[i + 0];
    const rp0y = rp3[i + 1];
    const rp0z = rp3[i + 2];

    const rp1x = rp3[i + 3];
    const rp1y = rp3[i + 4];
    const rp1z = rp3[i + 5];

    const l0x = l3[i + 0];
    const l0y = l3[i + 1];
    const l0z = l3[i + 2];

    const l1x = l3[i + 3];
    const l1y = l3[i + 4];
    const l1z = l3[i + 5];

    const r0x = r3[i + 0];
    const r0y = r3[i + 1];
    const r0z = r3[i + 2];

    const r1x = r3[i + 3];
    const r1y = r3[i + 4];
    const r1z = r3[i + 5];

    const leftFacePoints = [l1x, l1y, l1z, l0x, l0y, l0z];
    const rightFacePoints = [r0x, r0y, r0z, r1x, r1y, r1z];

    const trucatedEdgeStart = i === 0 && truncatedStartPeakDist > DIST_EPSILON;
    const trucatedEdgeEnd = i === l - 3 && truncatedEndPeakDist > DIST_EPSILON;

    if (trucatedEdgeEnd) {
      endHipFace = faceByThreePointsOnRails(l3, r3, lp3, lp3.length - 3);
    }

    leftFacePoints.push(lp0x, lp0y, lp0z);
    leftFacePoints.push(lp1x, lp1y, lp1z);

    rightFacePoints.push(rp1x, rp1y, rp1z);
    rightFacePoints.push(rp0x, rp0y, rp0z);

    if (trucatedEdgeStart) {
      startHipFace = faceByThreePointsOnRails(r3, l3, lp3, 0);
    }

    const rightFace = faceByNPoints(rightFacePoints);
    const leftFace = faceByNPoints(leftFacePoints);

    if (leftFace) {
      leftFaces.push(leftFace);
    }
    if (rightFace) {
      rightFaces.push(rightFace);
    }
  }

  return {
    startFace,
    startHipFace: startHipFace ?? undefined,
    leftFaces,
    rightFaces,
    leftMassFaces,
    rightMassFaces,
    bottomFaces,
    endFace,
    endHipFace: endHipFace ?? undefined,
  };
}
